import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { VerticalChartObject } from '../../../../shared/component/vertical-charts/vertical-charts.component';
import { OrganizationsService } from '../../service/organizations.service';
import { ChangeRouteAndSwitchFund } from '../../../../../core/component/hornavbar/horizontal-nav-bar.component';
import { ToolTipSettings } from '../../../../shared/component/tooltip/tooltip.util';
import { Fund } from 'src/app/domain/fund-performance/dto/Fund';
import { OrganizationPageService } from '../../service/organization-page.service';
import { ExcelService } from 'src/app/domain/excel';
import { ExcelTemplateName } from 'src/app/domain/excel/interfaces';
import { OrgOverviewCalcsService } from '../org-overview-calcs.service';
import {
  FpOverviewData, FpOverviewDataShownGroups,
  FundPerformanceOverview,
  ParsedFund,
  SelectedFundsAmount,
  ShowOrgOverview,
  FundPerformanceOverviewSorting,
  FundsAndPerformanceExportExcelWrapper,
  generateGraphicData, GroupedOverviewTotal,
} from '../utils';
import { Organization } from '../../../dto/Organization';
import { FundPerformanceBackendService } from 'src/app/domain/fund-performance/service/fund-performance-backend.service';
import {
  getFundPerfTooltipText,
} from '../../../../fund-performance/component/fund-performances/pojo/fp-export-classes';
import { FP_URLS } from "../../../../fund-performance/component/fund-performances/pojo/FP_URLS";
import { DateService } from "../../../../../util/common/date-service/date.service";
import { AuthService } from "../../../../auth/service/auth.service";
import { HorNavbarService } from "../../../../../core/service/hor-navbar.service";
import { SwitcherNumberType } from "../../../../../core/component/numberswitcher/utils/number-switcher-common-classes";
import {
  FundsSelectorDropdownService,
} from "../../../../shared/component/funds-selector-dropdown/FundsSelectorDropdown.service";
import { FundPerformance } from '../../../../fund-performance/dto/FundPerformance';
import { SkeletonProps } from "../../../../standard-components/skeleton-table/standard-skeleton-table.component";

@Component({
  selector: 'app-organization-overview',
  templateUrl: './organization-overview.component.html',
  styleUrls: ['./organization-overview.component.scss'],
})
export class OrganizationOverviewComponent implements OnInit, OnChanges, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  private fetchAllFundsFPS = new BehaviorSubject<ParsedFund[]>([]);
  protected readonly skeletonProps: SkeletonProps = {
    tdAmount: 9,
    trAmount: 5,
    headers: [''].constructor(9) as string[],
  };

  @Input() isAdmin = false;
  @Input() selectedOrganization: Organization;
  numberSwitcher: SwitcherNumberType = SwitcherNumberType.REGULAR;

  fundsData = new FpOverviewData();
  graphicData: VerticalChartObject;
  totals = new GroupedOverviewTotal();
  selectedFundsAmount = new SelectedFundsAmount();
  selectedFundsUUIDs: string[] = [];
  show = new ShowOrgOverview();
  shownGroups = FpOverviewDataShownGroups;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Math = Math;
  commonReportedPeriod = DateService.getLastCompletedQuarterEndDate(new Date());

  toolTipSettings: ToolTipSettings = {
    show: false,
    title: '',
  };

  readonly MAX_FUND_NAME_LEN = 60;

  protected readonly FundPerformanceOverviewSorting = FundPerformanceOverviewSorting;
  isToolTipForFundName = false;

  constructor(
    private fpService: FundPerformanceBackendService,
    private orgService: OrganizationsService,
    private orgPageService: OrganizationPageService,
    private excelService: ExcelService,
    private authService: AuthService,
    private horNavbarService: HorNavbarService,
    private fundsSelectorDropdownService: FundsSelectorDropdownService,
  ) {
  }

  ngOnInit(): void {
    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream))
      .subscribe((userType) => {
        this.isAdmin = userType === 'admin';
      });

    this.horNavbarService.getNumberSwitcher.pipe(takeUntil(this._destroyStream)).subscribe((ns) => {
      if (!ns) {
        return;
      }
      this.numberSwitcher = ns;
    });

    this.fundsSelectorDropdownService.fundsData
      .pipe(takeUntil(this._destroyStream))
      .subscribe((fundsData) => {
        if (!fundsData) {
          return;
        }
        this.fundsData = fundsData;
        this.fundsSelectorDropdownService.findSelected(this.selectedFundsAmount);
        this.updateOverview();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedOrganization) {
      this.show.loading = true;
      this.resetData();
      const parsedFunds = this.parseFunds();
      this.getFP(parsedFunds).then(() => this.show.loading = false);
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  exportExcel(): void {
    const fileName = this.selectedOrganization.name + '-fund-performance.xlsx';
    const dataForExport: FundsAndPerformanceExportExcelWrapper = {
      funds: this.fundsData.all,
      totals: this.totals.all,
    };
    this.excelService.exportByTemplate(dataForExport, fileName, ExcelTemplateName.fundAndPerformance);
  }

  changePeriod(period: Date, fpOverview: FundPerformanceOverview): void {
    fpOverview.selectedFP = fpOverview.fps.find((fp) => fp.period === period);
    this.updateIrr(fpOverview.selectedFP);
    this.updateCarry(fpOverview);
    this.updateOverview();
  }

  changeCommonReportedPeriod(period: string): void {
    if (!period) {
      return;
    }
    this.fundsData.all.forEach((fpo) => {
      fpo.selectedFP = this.findClosestFP(fpo.fps, new Date(period).getTime());
      this.updateCarry(fpo);
    });
    this.updateOverview();
  }

  goToFundPerformance(selectedFP: FundPerformanceOverview): void {
    const data: ChangeRouteAndSwitchFund = {
      fund: this.selectedOrganization.funds.find((f) => f.uuid === selectedFP.fundUuid),
      orgUuid: this.selectedOrganization.uuid,
      fp: selectedFP.selectedFP,
      route: FP_URLS.BASE,
    };
    this.orgService.setEmulateSwitchFund(data);
  }

  activateTooltip(id: string, text?: string): void {
    this.isToolTipForFundName = id.includes('fundName');
    if (this.isToolTipForFundName && text.length < this.MAX_FUND_NAME_LEN) {
      return;
    }
    this.toolTipSettings.parentId = id;
    this.toolTipSettings.show = true;
    this.toolTipSettings.title = this.isToolTipForFundName ? text : getFundPerfTooltipText(id);
    this.toolTipSettings.coordinates = document.getElementById(id)?.getBoundingClientRect();
  }

  hideTooltip(): void {
    this.toolTipSettings.show = false;
  }

  async fundsDataChangedByUser(fundsData: FpOverviewData): Promise<void> {
    await this.updateSelectedFunds(fundsData.all
      .filter((data) => data.isShown)
      .map((data) => data.fundUuid));
  }

  private createCharts(): void {
    if (!this.fundsData.all) {
      this.graphicData = null;
      return;
    }
    const funds = [...this.fundsData.funds, ...this.fundsData.spvs].filter((f) => f.isShown);
    this.graphicData = generateGraphicData(funds);
  }

  private resetData(): void {
    this.fundsSelectorDropdownService.setFundsData(new FpOverviewData());
    this.fundsData = new FpOverviewData();
    this.graphicData = undefined;
    this.totals = new GroupedOverviewTotal();
  }

  private parseFunds(): ParsedFund[] {
    return this.selectedOrganization.funds.map((fund: Fund) => (
      {
        name: fund.name,
        id: fund.id,
        uuid: fund.uuid,
        initialClosing: this.getRegistrationDateOrInitialClosing(fund),
        isSpv: fund.spv,
        fps: null,
        orderInOrgOverviewTable: fund.order.inOrgOverviewTable,
      }));
  }

  private getRegistrationDateOrInitialClosing(fund: Fund): Date {
    return fund.initialClosing ? fund.initialClosing : fund.registrationDate;
  }

  // Resets and gets new FundPerformances from all funds in the org. Cancels prev requests.
  private async getFP(parsedFunds: ParsedFund[]): Promise<void> {
    if (!parsedFunds.length) {
      return;
    }
    const funds = await this.fetchFundPerformances(parsedFunds);
    const preparedFPs: FundPerformanceOverview[] = await this.prepareFetchedFundFPs(funds);
    // Sort and update funds
    this.sortFPs(preparedFPs);
    const fundsData = new FpOverviewData();
    fundsData.all = preparedFPs;
    preparedFPs.forEach((f) => (f.isSpv ? fundsData.spvs.push(f) : fundsData.funds.push(f)));
    this.show.funds = Boolean(fundsData.funds.length);
    this.show.spvs = Boolean(fundsData.spvs.length);
    this.fundsSelectorDropdownService.setFundsData(fundsData);
    this.updateOverview();
    this.fetchAllFundsFPS.next(parsedFunds);
  }

  // Gets FundPerformances for each fund and then creates charts as soon as all fps are loaded.
  private async fetchFundPerformances(parsedFunds: ParsedFund[]): Promise<ParsedFund[]> {
    const selectedDate = DateService.toHyphenDate(new Date(this.commonReportedPeriod));
    return firstValueFrom(this.fpService.getAllFundPerformancesByOrgUuid(this.selectedOrganization.uuid, selectedDate)
      .pipe(
        map((response) =>
          parsedFunds.map((fund) => ({
            ...fund,
            fps: response?.filter((fp) => fp.isVisible && fp.fundUuid === fund.uuid) || [],
          })),
        ),
        takeUntil(this._destroyStream),
      ));
  }

  private fetchSelectedFunds(orgUUID: string): Promise<string[]> {
    return firstValueFrom(
      this.fpService.getSelectedFunds(orgUUID)
        .pipe(
          takeUntil(this._destroyStream),
          map((selectedFundsWrapper) => {
            this.selectedFundsUUIDs = selectedFundsWrapper?.selectedFundsUuids ?? [];
            return this.selectedFundsUUIDs;
          })
        )
    );
  }

  private async updateSelectedFunds(newUUIDs: string[]): Promise<void> {
    this.selectedFundsUUIDs = [...newUUIDs];
    await firstValueFrom(this.fpService.setSelectedFunds({
      orgUuid: this.selectedOrganization.uuid,
      selectedFundsUuids: [...newUUIDs],
    }));
  }

  private async prepareFetchedFundFPs(fetchedFunds: ParsedFund[]): Promise<FundPerformanceOverview[]> {
    const selectedDate: number = new Date(this.commonReportedPeriod).getTime();
    await this.fetchSelectedFunds(this.selectedOrganization.uuid);
    return fetchedFunds
      .filter((fund) => fund.fps?.length > 0) // filter out funds without fps
      .map((fund) => {
        const fps = fund.fps;
        const closestFP = this.findClosestFP(fps, selectedDate);
        const overview: FundPerformanceOverview = {
          isSpv: fund.isSpv,
          orderInTable: fund.orderInOrgOverviewTable,
          name: fund.name,
          fundUuid: fund.uuid,
          isShown: this.selectedFundsUUIDs.includes(fund.uuid),
          selectedFP: closestFP,
          fps,
          periods: fps.map((fp) => fp.period),
          initialClosing: fund.initialClosing,
          beforeCarry: OrgOverviewCalcsService.calcBeforeCarry(closestFP),
          afterCarry: OrgOverviewCalcsService.calcAfterCarry(closestFP),
        };
        return overview;
      });
  }

  private sortFPs(fps: FundPerformanceOverview[]): void {
    fps.sort((a, b) =>
      new Date(b.initialClosing).getTime() - new Date(a.initialClosing).getTime());

    fps.sort((a, b) => a.orderInTable - b.orderInTable);
  }

  private calculateShownTotals(): void {
    const all = this.fundsData.all.filter((f) => f.isShown);
    const funds = this.fundsData.funds.filter((f) => f.isShown);
    const spvs = this.fundsData.spvs.filter((f) => f.isShown);
    this.totals.all = OrgOverviewCalcsService.calcTotals(all);
    this.totals.funds = OrgOverviewCalcsService.calcTotals(funds);
    this.totals.spvs = OrgOverviewCalcsService.calcTotals(spvs);
  }

  private updateOverview(): void {
    this.orgPageService.setFpOverview(this.fundsData.all.filter((fund) => fund.isShown));
    this.calculateShownTotals();
    this.createCharts();
  }

  private findClosestFP(fps: FundPerformance[], today: number): FundPerformance {
    return fps.reduce((closest, current) => {
      const currentPeriod = new Date(current.period).getTime();
      const closestPeriod = new Date(closest.period).getTime();
      return Math.abs(currentPeriod - today) < Math.abs(closestPeriod - today) ? current : closest;
    }, fps[0]);
  }

  private updateCarry(fpOverview: FundPerformanceOverview): void {
    fpOverview.beforeCarry = OrgOverviewCalcsService.calcBeforeCarry(fpOverview.selectedFP);
    fpOverview.afterCarry = OrgOverviewCalcsService.calcAfterCarry(fpOverview.selectedFP);
  }

  /**
   * Update IRR value in selected FP if the manual IRR is not provided
   *
   * @param fp FundPerformance where IRR should be updated
   */
  private updateIrr(fp: FundPerformance): FundPerformance {
    if (fp.netIrr === null) {
      this.calculateIrr(fp).subscribe((updatedFP) => {
        fp = updatedFP;
      });
    }
    return fp;
  }

  /**
   * Get calculated Irr values before carry and after carry for selected FP
   *
   * @param fp FundPerformance where IRR should be updated
   */
  private calculateIrr(fp: FundPerformance): Observable<FundPerformance> {
    return this.fpService.calculateIrr(fp, this.selectedOrganization.selectedFund.uuid)
      .pipe(
        takeUntil(this._destroyStream),
        map((irr) => {
          fp.irrCalculated = irr.irrCanBeCalculated ? irr.irr : null;
          fp.irrCalculatedAfterCarry = irr.irrCanBeCalculated ? irr.irrAfterCarry : null;
          return fp;
        })
      );
  }
}
